import { CollapsibleCard } from '@/components/ui/CollapsibleCard'
import { DisabledSurfaceOverlay } from '@/components/patterns/disabled-surface-overlay'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, ReadOnly, useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  useCreateDesignMutation,
  useDeleteDesignMutation,
  useUpdateDesignMutation,
  useUpdateWebsiteBuilderMutation,
} from '@/lib/services/designs/designs.query'
import { useOccasions } from '@/lib/services/occasions/occasions.query'
import { buildOccasionSelectOptions } from '@/lib/services/occasions/occasions.utils'
import { useUpdateStoreMutation } from '@/lib/services/stores/stores.query'
import {
  DESIGN_SCHEDULE_END_OFFSET_REQUIRED_ERROR_MESSAGE,
  DESIGN_SCHEDULE_START_OFFSET_REQUIRED_ERROR_MESSAGE,
  NON_DEFAULT_OVERRIDE_REQUIRED_ERROR_MESSAGE,
  nonDefaultDesignOverridesSchema,
} from '@/lib/services/designs/designs.schema'
import { cn, getErrorMessage } from '@/lib/utils'
import { revalidateLogic, useStore } from '@tanstack/react-form'
import { useNavigate, useParams } from '@tanstack/react-router'
import {
  BookOpen,
  Calendar,
  Flower,
  Footprints,
  MessageCircleHeart,
  Package,
  Palette,
  PanelTop,
  Star,
} from 'lucide-react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'sonner'
import DeleteButton from '../DeleteButton'
import SaveBar from '../SaveBar'
import AboutUsPageForm from './AboutUsPageForm'
import BrandStyleForm from './BrandStyleForm'
import ContactUsPageForm from './ContactUsPageForm'
import FeaturedContentForm, { SeasonalFeaturedCollectionForm } from './FeaturedContentForm'
import FooterSectionForm from './FooterSectionForm'
import GalleryPageForm from './GalleryPageForm'
import HeaderSectionForm from './HeaderSectionForm'
import HeroSectionForm from './HeroSectionForm'
import ProductPagesForm from './ProductPagesForm'
import ScheduleForm from './ScheduleForm'
import SeasonalDesignWorkspace from './SeasonalDesignWorkspace'
import WebsiteDesignWorkspace from './WebsiteDesignWorkspace'
import { applySortOrder, buildDesignSubmitPayload, NO_OCCASION_OPTION_ID } from './designForm.utils'
import {
  getHomepageHeroLayout,
  getSavedDesignValues,
  normalizeDesignValues,
} from './designFormValues'
import { useCollectionMediaDrafts } from './collectionMediaDrafts'
import {
  clearWorkspaceDraftValues,
  readWorkspaceDraftSnapshot,
  writeWorkspaceDraftValues,
} from './websiteDesignWorkspaceStorage'
import { INHERIT_HERO_LAYOUT } from './storefrontStyleOptions'

const WORKSPACE_DRAFT_WRITE_DEBOUNCE_MS = 150

const getDesignWorkspaceDraftValues = (values) => {
  const designValues = { ...values }
  delete designValues.store_settings

  return designValues
}

const getCollectionMediaSuccessMessage = (savedCount) =>
  savedCount === 1
    ? 'Collection image updated successfully'
    : 'Collection images updated successfully'

const getDesignSaveSuccessMessage = ({ action, savedCollectionMediaCount }) => {
  const designMessage = `Design ${action}d successfully`

  if (savedCollectionMediaCount === 0) {
    return designMessage
  }

  return savedCollectionMediaCount === 1
    ? `Design ${action}d and collection image updated successfully`
    : `Design ${action}d and collection images updated successfully`
}

const getDesignSaveFailureMessage = (savedCollectionMediaCount) => {
  if (savedCollectionMediaCount === 0) {
    return 'Design save failed'
  }

  return savedCollectionMediaCount === 1
    ? 'Design save failed after collection image was updated'
    : 'Design save failed after collection images were updated'
}

const getDeliveryBannerSaveSuccessMessage = ({ savedDesign, savedCollectionMediaCount }) => {
  if (savedCollectionMediaCount === 0) {
    return savedDesign
      ? 'Website and same-day delivery banner updated successfully'
      : 'Same-day delivery banner updated successfully'
  }

  const collectionImageLabel =
    savedCollectionMediaCount === 1 ? 'collection image' : 'collection images'

  return savedDesign
    ? `Website, same-day delivery banner, and ${collectionImageLabel} updated successfully`
    : `Same-day delivery banner and ${collectionImageLabel} updated successfully`
}

const getDeliveryBannerSaveFailureMessage = ({ savedDesign, savedCollectionMediaCount }) => {
  const failureMessage = savedDesign
    ? 'Website save failed'
    : 'Same-day delivery banner update failed'

  if (savedCollectionMediaCount === 0) {
    return failureMessage
  }

  return savedCollectionMediaCount === 1
    ? `${failureMessage} after collection image was updated`
    : `${failureMessage} after collection images were updated`
}

const OverrideSectionState = ({ isEnabled, disabledLabel, children }) => {
  return (
    <div className="mt-5">
      <div className="relative rounded-lg border border-border/70 bg-muted/20 p-4">
        {!isEnabled && <DisabledSurfaceOverlay label={disabledLabel} />}

        <div className={isEnabled ? '' : 'opacity-55 saturate-0'}>
          <ReadOnly isReadOnly={!isEnabled}>{children}</ReadOnly>
        </div>
      </div>
    </div>
  )
}

const OverrideValidationToast = ({ form, isEnabled }) => {
  const submissionAttempts = useStore(form.store, (state) => state.submissionAttempts)
  const submitErrors = useStore(form.store, (state) => state.errors)
  const lastToastedAttemptRef = useRef(0)

  useEffect(() => {
    if (!isEnabled || submissionAttempts <= 0) {
      return
    }

    if (lastToastedAttemptRef.current === submissionAttempts) {
      return
    }

    const errorMessage = getErrorMessage(submitErrors)
    const hasOverrideRequiredError =
      typeof errorMessage === 'string' &&
      errorMessage.includes(NON_DEFAULT_OVERRIDE_REQUIRED_ERROR_MESSAGE)
    const hasScheduleEndOffsetError =
      typeof errorMessage === 'string' &&
      errorMessage.includes(DESIGN_SCHEDULE_END_OFFSET_REQUIRED_ERROR_MESSAGE)
    const hasScheduleStartOffsetError =
      typeof errorMessage === 'string' &&
      errorMessage.includes(DESIGN_SCHEDULE_START_OFFSET_REQUIRED_ERROR_MESSAGE)

    if (!hasOverrideRequiredError && !hasScheduleStartOffsetError && !hasScheduleEndOffsetError) {
      return
    }

    lastToastedAttemptRef.current = submissionAttempts
    toast.error(
      hasScheduleStartOffsetError
        ? DESIGN_SCHEDULE_START_OFFSET_REQUIRED_ERROR_MESSAGE
        : hasScheduleEndOffsetError
          ? DESIGN_SCHEDULE_END_OFFSET_REQUIRED_ERROR_MESSAGE
          : NON_DEFAULT_OVERRIDE_REQUIRED_ERROR_MESSAGE
    )
  }, [isEnabled, submissionAttempts, submitErrors])

  return null
}

const WorkspaceDraftPersistence = ({
  form,
  organizationId,
  designId,
  baselineValues,
  designValuesOnly = false,
  initialValuesCameFromWorkspaceDraftRef,
  isWritePausedRef,
}) => {
  const currentFormValues = useStore(form.store, (state) => state.values)
  const isFormDefaultValue = useStore(form.store, (state) => state.isDefaultValue)
  const pendingDraftValuesRef = useRef(null)
  const draftWriteTimerRef = useRef(null)

  const cancelScheduledDraftWrite = useEffectEvent(() => {
    if (draftWriteTimerRef.current) {
      window.clearTimeout(draftWriteTimerRef.current)
      draftWriteTimerRef.current = null
    }
  })

  const flushPendingDraftWrite = useEffectEvent(() => {
    cancelScheduledDraftWrite()

    const pendingValues = pendingDraftValuesRef.current
    pendingDraftValuesRef.current = null

    if (!pendingValues || isWritePausedRef.current) {
      return
    }

    writeWorkspaceDraftValues({
      organizationId,
      designId,
      baselineValues,
      values: pendingValues,
    })
  })

  useEffect(() => {
    if (isWritePausedRef.current) {
      pendingDraftValuesRef.current = null
      cancelScheduledDraftWrite()
      return
    }

    if (!isFormDefaultValue) {
      pendingDraftValuesRef.current = designValuesOnly
        ? getDesignWorkspaceDraftValues(currentFormValues)
        : currentFormValues
      cancelScheduledDraftWrite()
      draftWriteTimerRef.current = window.setTimeout(
        flushPendingDraftWrite,
        WORKSPACE_DRAFT_WRITE_DEBOUNCE_MS
      )
      return () => {
        if (draftWriteTimerRef.current) {
          window.clearTimeout(draftWriteTimerRef.current)
          draftWriteTimerRef.current = null
        }
      }
    }

    pendingDraftValuesRef.current = null
    cancelScheduledDraftWrite()

    if (!initialValuesCameFromWorkspaceDraftRef.current) {
      clearWorkspaceDraftValues({
        organizationId,
        designId,
      })
    }
  }, [
    currentFormValues,
    designId,
    designValuesOnly,
    initialValuesCameFromWorkspaceDraftRef,
    isFormDefaultValue,
    isWritePausedRef,
    organizationId,
  ])

  useEffect(
    () => () => {
      flushPendingDraftWrite()
    },
    []
  )

  return null
}

const DesignForm = ({
  initialData = null,
  templateData = null,
  defaultDesign = null,
  storeSettings = null,
  canEditStore = false,
  presentation = 'classic',
  workspaceSection = 'brand-layout',
  onWorkspaceSectionChange = null,
  seasonalNotice = null,
}) => {
  const path = usePathHelper()
  const navigate = useNavigate()
  const { orgId } = useParams({ strict: false })
  const { isReadOnly } = useIsReadOnly()

  const isEditing = !!initialData?.id
  const isDefault = !!initialData?.is_default

  const isWorkspaceMode = presentation === 'workspace' && isDefault
  const isSeasonalWorkspaceMode = presentation === 'seasonal-workspace' && !isDefault
  const isWorkspaceDraftEnabled = (isWorkspaceMode || isSeasonalWorkspaceMode) && !isReadOnly
  const createDesignMutation = useCreateDesignMutation()
  const updateDesignMutation = useUpdateDesignMutation()
  const updateWebsiteBuilderMutation = useUpdateWebsiteBuilderMutation()
  const updateStoreMutation = useUpdateStoreMutation()
  const deleteDesignMutation = useDeleteDesignMutation({
    cleanupOnSuccess: false,
  })
  const { data: occasions = [] } = useOccasions()
  const rebasedResetValuesRef = useRef(null)
  const collectionMediaDraftController = useCollectionMediaDrafts()
  const [isSeasonalRecommendationPending, setIsSeasonalRecommendationPending] = useState(false)

  const mutation = isEditing ? updateDesignMutation : createDesignMutation
  const workspaceDraftOrganizationId = orgId
  const workspaceDraftDesignId =
    initialData?.id ?? (isSeasonalWorkspaceMode ? 'seasonal-new' : 'default')
  const workspaceDraftContext = {
    organizationId: workspaceDraftOrganizationId,
    designId: workspaceDraftDesignId,
  }
  const sourceHeroLayout = getHomepageHeroLayout(initialData ?? templateData)
  const allowInheritedHeroLayout =
    !isDefault &&
    (initialData?.design_origin === 'platform' ||
      templateData?.design_origin === 'platform' ||
      sourceHeroLayout === INHERIT_HERO_LAYOUT)
  const defaultHeroLayout = null
  const designNormalizationOptions = {
    allowInheritedHeroLayout,
    defaultHeroLayout,
    initializeSeasonalAutomation: isSeasonalWorkspaceMode,
  }
  const normalizedInitialDesignValues = normalizeDesignValues(
    initialData,
    templateData,
    designNormalizationOptions
  )
  const initialSameDayDeliveryPromotionEnabled =
    storeSettings?.same_day_delivery_promotion_enabled ?? true
  const persistedSameDayDeliveryPromotionEnabledRef = useRef(initialSameDayDeliveryPromotionEnabled)
  const normalizedInitialFormValues = isWorkspaceMode
    ? {
        ...normalizedInitialDesignValues,
        store_settings: {
          same_day_delivery_promotion_enabled: initialSameDayDeliveryPromotionEnabled,
        },
      }
    : normalizedInitialDesignValues
  const [initialWorkspaceDraftSnapshot] = useState(() =>
    readWorkspaceDraftSnapshot({
      ...workspaceDraftContext,
      fallbackValues: normalizedInitialDesignValues,
      enabled: isWorkspaceDraftEnabled,
    })
  )
  const [hasWorkspaceDraft, setHasWorkspaceDraft] = useState(initialWorkspaceDraftSnapshot.hasDraft)
  const [rebasedDefaultValues, setRebasedDefaultValues] = useState(null)
  const initialValuesCameFromWorkspaceDraftRef = useRef(initialWorkspaceDraftSnapshot.hasDraft)
  const isWorkspaceDraftWritePausedRef = useRef(false)
  const workspaceDraftBaselineValues = isWorkspaceMode
    ? getDesignWorkspaceDraftValues(rebasedDefaultValues ?? normalizedInitialFormValues)
    : (rebasedDefaultValues ?? normalizedInitialFormValues)
  const initialWorkspaceDraftValues = isWorkspaceMode
    ? {
        ...initialWorkspaceDraftSnapshot.values,
        store_settings: normalizedInitialFormValues.store_settings,
      }
    : initialWorkspaceDraftSnapshot.values
  const initialFormValues = rebasedDefaultValues ?? initialWorkspaceDraftValues
  const occasionOptions = buildOccasionSelectOptions({
    occasions,
    selectedOccasionId: initialFormValues.occasion_id,
    selectedOccasion: initialData?.occasion,
    emptyOption: { id: NO_OCCASION_OPTION_ID, name: 'No occasion' },
  })
  const form = useAppForm({
    defaultValues: initialFormValues,
    validationLogic: isDefault
      ? undefined
      : revalidateLogic({
          mode: 'submit',
          modeAfterSubmission: 'change',
        }),
    validators: {
      onDynamic: isDefault ? undefined : nonDefaultDesignOverridesSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        const action = isEditing ? 'update' : 'create'
        let savedCollectionMediaCount = 0
        let wasSavingDesign = false
        let wasSavingStoreSettings = false

        try {
          const nextValue = JSON.parse(JSON.stringify(value))
          const submittedSameDayDeliveryPromotionEnabled =
            nextValue?.store_settings?.same_day_delivery_promotion_enabled
          const collectionVisibilityUpdates = Array.isArray(
            nextValue?.collection_visibility_updates
          )
            ? nextValue.collection_visibility_updates
            : []
          const hasCollectionVisibilityUpdates = collectionVisibilityUpdates.length > 0
          const shouldSaveStoreSettings =
            isWorkspaceMode &&
            canEditStore &&
            typeof submittedSameDayDeliveryPromotionEnabled === 'boolean' &&
            submittedSameDayDeliveryPromotionEnabled !==
              persistedSameDayDeliveryPromotionEnabledRef.current
          delete nextValue.store_settings
          delete nextValue.collection_visibility_updates

          const galleryMainBlocks = nextValue?.pages?.['/gallery']?.blocks?.main
          const galleryAlbumsBlock = Array.isArray(galleryMainBlocks)
            ? galleryMainBlocks.find((block) => block?.type === 'album')
            : null

          if (Array.isArray(galleryAlbumsBlock?.albums)) {
            galleryAlbumsBlock.albums = applySortOrder(galleryAlbumsBlock.albums)
          }

          const payload = buildDesignSubmitPayload({ value: nextValue })
          if (!isEditing) {
            payload.seasonal_automation = isSeasonalWorkspaceMode
          }
          const persistedDesignPayload = buildDesignSubmitPayload({
            value: rebasedResetValuesRef.current ?? normalizedInitialFormValues,
          })
          const hasPersistedDesignChanges =
            JSON.stringify(payload) !== JSON.stringify(persistedDesignPayload)
          const hasMediaDraftsAtSubmit =
            !isReadOnly && collectionMediaDraftController.hasCurrentDrafts()
          const shouldSaveDesign =
            !isReadOnly &&
            (hasCollectionVisibilityUpdates || shouldSaveStoreSettings
              ? hasPersistedDesignChanges || hasCollectionVisibilityUpdates
              : !hasMediaDraftsAtSubmit || !formApi.state.isDefaultValue)

          const collectionMediaSaveResult = hasMediaDraftsAtSubmit
            ? await collectionMediaDraftController.saveDrafts()
            : { savedCount: 0 }
          savedCollectionMediaCount = collectionMediaSaveResult.savedCount

          let response = null
          let updatedStore = null

          if (shouldSaveDesign && (shouldSaveStoreSettings || hasCollectionVisibilityUpdates)) {
            wasSavingDesign = true
            wasSavingStoreSettings = shouldSaveStoreSettings
            const websiteBuilderPayload = {
              ...payload,
              collection_visibility_updates: collectionVisibilityUpdates,
            }
            if (shouldSaveStoreSettings) {
              websiteBuilderPayload.store_settings = {
                same_day_delivery_promotion_enabled: submittedSameDayDeliveryPromotionEnabled,
              }
            }
            response = await updateWebsiteBuilderMutation.mutateAsync(websiteBuilderPayload)
            updatedStore = shouldSaveStoreSettings
              ? {
                  same_day_delivery_promotion_enabled: submittedSameDayDeliveryPromotionEnabled,
                }
              : null
          } else if (shouldSaveDesign) {
            response = await mutation.mutateAsync(payload)
          } else if (shouldSaveStoreSettings) {
            wasSavingStoreSettings = true
            updatedStore = await updateStoreMutation.mutateAsync({
              same_day_delivery_promotion_enabled: submittedSameDayDeliveryPromotionEnabled,
            })
          }

          const savedSameDayDeliveryPromotionEnabled = shouldSaveStoreSettings
            ? (updatedStore?.same_day_delivery_promotion_enabled ??
              submittedSameDayDeliveryPromotionEnabled)
            : persistedSameDayDeliveryPromotionEnabledRef.current

          persistedSameDayDeliveryPromotionEnabledRef.current = savedSameDayDeliveryPromotionEnabled
          toast.success(
            shouldSaveStoreSettings
              ? getDeliveryBannerSaveSuccessMessage({
                  savedDesign: shouldSaveDesign,
                  savedCollectionMediaCount,
                })
              : shouldSaveDesign
                ? getDesignSaveSuccessMessage({
                    action,
                    savedCollectionMediaCount,
                  })
                : getCollectionMediaSuccessMessage(savedCollectionMediaCount)
          )
          collectionMediaDraftController.clearDrafts()

          if (!shouldSaveDesign && !shouldSaveStoreSettings) {
            return
          }

          const savedDesignValues = getSavedDesignValues({
            response,
            submittedValue: nextValue,
            normalizationOptions: {
              ...designNormalizationOptions,
              defaultHeroLayout: null,
            },
          })
          const savedFormValues = isWorkspaceMode
            ? {
                ...savedDesignValues,
                store_settings: {
                  same_day_delivery_promotion_enabled: savedSameDayDeliveryPromotionEnabled,
                },
              }
            : savedDesignValues
          rebasedResetValuesRef.current = savedFormValues
          setRebasedDefaultValues(savedFormValues)
          isWorkspaceDraftWritePausedRef.current = true
          initialValuesCameFromWorkspaceDraftRef.current = false
          setHasWorkspaceDraft(false)
          if (isWorkspaceDraftEnabled) {
            clearWorkspaceDraftValues(workspaceDraftContext)
          }

          formApi.reset(savedFormValues)
          setTimeout(() => {
            formApi.reset(savedFormValues)
            isWorkspaceDraftWritePausedRef.current = false
          })

          if (!isEditing && response?.id) {
            void navigate({
              ...path.designs.show(response?.id).link,
              ignoreBlocker: true,
            })
          }
        } catch (error) {
          toast.error(
            wasSavingStoreSettings
              ? getDeliveryBannerSaveFailureMessage({
                  savedDesign: wasSavingDesign,
                  savedCollectionMediaCount,
                })
              : error?.collectionMediaSaveFailed
                ? error?.collectionMediaPartialFailure
                  ? 'Some collection images could not be saved'
                  : 'Collection image save failed'
                : getDesignSaveFailureMessage(savedCollectionMediaCount),
            {
              description: getErrorMessage(error),
            }
          )

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const homePage = form.getFieldValue('pages./')
  const aboutUsPage = form.getFieldValue('pages./about-us')
  const galleryPage = form.getFieldValue('pages./gallery')
  const contactUsPage = form.getFieldValue('pages./contact-us')

  const handleReset = () => {
    isWorkspaceDraftWritePausedRef.current = true
    initialValuesCameFromWorkspaceDraftRef.current = false
    setHasWorkspaceDraft(false)
    collectionMediaDraftController.clearDrafts()
    if (isWorkspaceDraftEnabled) {
      clearWorkspaceDraftValues(workspaceDraftContext)
    }

    const resetValues = rebasedResetValuesRef.current ?? normalizedInitialFormValues

    rebasedResetValuesRef.current = resetValues
    setRebasedDefaultValues(resetValues)
    form.reset(resetValues)
    setTimeout(() => {
      form.reset(resetValues)
      if (isWorkspaceDraftEnabled) {
        clearWorkspaceDraftValues(workspaceDraftContext)
      }
      isWorkspaceDraftWritePausedRef.current = false
    })
  }

  const shouldBlockDesignNavigation = ({ next }) => {
    if (!isWorkspaceMode) {
      return true
    }

    const nextPath = String(next?.fullPath ?? next?.pathname ?? next?.href ?? next?.to ?? '').split(
      /[?#]/
    )[0]

    return !(
      nextPath.endsWith(`/${orgId}/website/builder`) || nextPath.endsWith('/website/builder')
    )
  }

  const deleteDesignAction =
    isEditing && !initialData?.is_default ? (
      <Can do="delete" on="designs">
        <DeleteButton
          id={initialData?.id}
          model={isSeasonalWorkspaceMode ? 'Seasonal Design' : 'Design'}
          mutation={deleteDesignMutation}
          path={path.designs.index()}
          replace
          onDeleted={deleteDesignMutation.cleanupDeletedDesign}
        />
      </Can>
    ) : null

  return (
    <div
      className={cn(
        isWorkspaceMode
          ? 'h-full min-h-0 overflow-hidden'
          : isSeasonalWorkspaceMode
            ? 'min-h-0 pb-6 xl:h-[calc(100dvh-14.5rem)] xl:pb-0'
            : 'pb-12',
        !isWorkspaceMode && isDefault && '-mt-10'
      )}
    >
      <OverrideValidationToast form={form} isEnabled={!isDefault} />

      <form.AppForm>
        <form.Form
          className={cn(
            isWorkspaceMode && 'h-full min-h-0',
            isSeasonalWorkspaceMode && 'min-h-0 xl:h-full xl:min-h-0'
          )}
        >
          {isWorkspaceDraftEnabled ? (
            <WorkspaceDraftPersistence
              form={form}
              organizationId={workspaceDraftOrganizationId}
              designId={workspaceDraftDesignId}
              baselineValues={workspaceDraftBaselineValues}
              designValuesOnly={isWorkspaceMode}
              initialValuesCameFromWorkspaceDraftRef={initialValuesCameFromWorkspaceDraftRef}
              isWritePausedRef={isWorkspaceDraftWritePausedRef}
            />
          ) : null}

          {isWorkspaceMode ? (
            <WebsiteDesignWorkspace
              form={form}
              workspaceSection={workspaceSection}
              onWorkspaceSectionChange={onWorkspaceSectionChange}
              getPersistedGalleryValues={() =>
                rebasedResetValuesRef.current ?? form.options.defaultValues
              }
              onRebasePersistedGalleryValues={(nextValues) => {
                rebasedResetValuesRef.current = nextValues
                setRebasedDefaultValues(nextValues)
              }}
              collectionMediaDraftController={collectionMediaDraftController}
            />
          ) : isSeasonalWorkspaceMode ? (
            <SeasonalDesignWorkspace
              form={form}
              defaultDesign={defaultDesign ?? templateData ?? null}
              deleteAction={deleteDesignAction}
              selectedSeasonalTag={
                initialData?.seasonal?.seasonal_tag ?? initialData?.occasion ?? null
              }
              seasonalTagFirstSetup={!isEditing}
              allowInheritedHeroLayout={allowInheritedHeroLayout}
              collectionMediaDraftController={collectionMediaDraftController}
              notice={seasonalNotice}
              onRecommendationPendingChange={setIsSeasonalRecommendationPending}
            />
          ) : (
            <>
              {!isDefault && (
                <div className="grid gap-y-4">
                  <form.AppField name="name">
                    {(field) => (
                      <field.TextInput
                        label="Design Name"
                        autoFocus={!field.state.value}
                        disabled={field.state.disabled}
                        placeholder="Example: Valentine's Day"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="occasion_id">
                    {(field) => (
                      <field.OccasionPicker
                        label="Occasion"
                        options={occasionOptions}
                        mode="single"
                        dialogDescription="Choose the occasion this seasonal website design belongs to."
                      />
                    )}
                  </form.AppField>
                </div>
              )}

              {isDefault ? (
                <>
                  <div className="my-6">
                    <Typography variant="h4">Sitewide Style</Typography>
                    <Typography variant="supporting">
                      These settings apply across your entire website.
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-y-4 mt-4">
                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Palette className="mr-2 size-6" />
                          Brand & Theme
                        </div>
                      }
                      description="Upload logos, control how they appear, and choose your storefront color theme."
                      persistKey="brand-theme"
                      defaultOpen
                    >
                      <BrandStyleForm />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <PanelTop className="mr-2 size-6" />
                          Header & Navigation
                        </div>
                      }
                      description="Edit your announcement bar and main menu links."
                      persistKey="header-navigation"
                    >
                      <HeaderSectionForm />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Footprints className="mr-2 size-6" />
                          Footer & Store Details
                        </div>
                      }
                      description="Control social links and the Visit Our Shop details shown to customers."
                      persistKey="footer-store-details"
                    >
                      <FooterSectionForm />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Package className="mr-2 size-6" />
                          Product Disclaimer
                        </div>
                      }
                      description="Set your substitution notice and choose which Product Types should show it."
                      persistKey="product-disclaimer"
                    >
                      <ProductPagesForm />
                    </CollapsibleCard>
                  </div>

                  <div className="my-6">
                    <Typography variant="h4">
                      Page: {homePage?.title}{' '}
                      <Typography variant="inlineCode">{homePage?.slug}</Typography>
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-y-4 mt-4">
                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Flower className="mr-2 size-6" />
                          Homepage banner
                        </div>
                      }
                      description="Highlight seasonal promotions with a photo, message, and call-to-action."
                      persistKey="hero-banner"
                    >
                      <HeroSectionForm />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Star className="mr-2 size-6" />
                          Featured Content
                        </div>
                      }
                      description="Choose the product, collection, gallery, and Contact Us sections shoppers see below the main homepage content."
                      persistKey="featured-content-home"
                    >
                      <FeaturedContentForm
                        pageSlug="/"
                        collectionMediaDraftController={collectionMediaDraftController}
                      />
                    </CollapsibleCard>
                  </div>

                  <div className="my-6">
                    <Typography variant="h4">
                      Page: {aboutUsPage?.title}{' '}
                      <Typography variant="inlineCode">{aboutUsPage?.slug}</Typography>
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-y-4 mt-4">
                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <BookOpen className="mr-2 size-6" />
                          About Us Story
                        </div>
                      }
                      description="Add an optional photo and story above your locations list."
                      persistKey="about-us-story"
                    >
                      <AboutUsPageForm />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Star className="mr-2 size-6" />
                          Featured Content
                        </div>
                      }
                      description="Add product, collection, gallery, or Contact Us sections below your About Us story and locations."
                      persistKey="featured-content-about"
                    >
                      <FeaturedContentForm
                        pageSlug="/about-us"
                        collectionMediaDraftController={collectionMediaDraftController}
                      />
                    </CollapsibleCard>
                  </div>

                  <div className="my-6">
                    <Typography variant="h4">
                      Page: {galleryPage?.title}{' '}
                      <Typography variant="inlineCode">{galleryPage?.slug}</Typography>
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-y-4 mt-4">
                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <BookOpen className="mr-2 size-6" />
                          Gallery Page Settings
                        </div>
                      }
                      description="Control the Gallery page announcement link, heading, and album content."
                      persistKey="gallery-page-settings"
                      defaultOpen={false}
                    >
                      <GalleryPageForm
                        getPersistedValues={() =>
                          rebasedResetValuesRef.current ?? form.options.defaultValues
                        }
                        onRebasePersistedValues={(nextValues) => {
                          rebasedResetValuesRef.current = nextValues
                          setRebasedDefaultValues(nextValues)
                        }}
                      />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Star className="mr-2 size-6" />
                          Featured Content
                        </div>
                      }
                      description="Add product, collection, or Contact Us sections below your gallery page content."
                      persistKey="featured-content-gallery"
                    >
                      <FeaturedContentForm
                        pageSlug="/gallery"
                        collectionMediaDraftController={collectionMediaDraftController}
                      />
                    </CollapsibleCard>
                  </div>

                  <div className="my-6">
                    <Typography variant="h4">
                      Page: {contactUsPage?.title}{' '}
                      <Typography variant="inlineCode">{contactUsPage?.slug}</Typography>
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-y-4 mt-4">
                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <MessageCircleHeart className="mr-2 size-6" />
                          Contact Us Page Settings
                        </div>
                      }
                      description="Control the Contact Us announcement link and page copy."
                      persistKey="contact-us-page-settings"
                      defaultOpen={false}
                    >
                      <ContactUsPageForm />
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Star className="mr-2 size-6" />
                          Featured Content
                        </div>
                      }
                      description="Add product, collection, or gallery sections below your Contact form."
                      persistKey="featured-content-contact"
                    >
                      <FeaturedContentForm
                        pageSlug="/contact-us"
                        collectionMediaDraftController={collectionMediaDraftController}
                      />
                    </CollapsibleCard>
                  </div>
                </>
              ) : (
                <>
                  <div className="my-6">
                    <Typography variant="h4">Seasonal Override Sections</Typography>
                    <Typography variant="supporting">
                      These sections can temporarily override your default design while this design
                      is active.
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-y-4 mt-4">
                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <PanelTop className="mr-2 size-6" />
                          Header & Navigation
                        </div>
                      }
                      description="Override the top banner and main navigation links."
                      persistKey="non-default-header-navigation"
                      defaultOpen
                    >
                      <form.AppField
                        name="section_overrides.header_banner"
                        listeners={{
                          onChange: ({ value }) => {
                            form.setFieldValue('section_overrides.header_brand', false)
                            form.setFieldValue('section_overrides.header_nav_menu', !!value)
                          },
                        }}
                      >
                        {(field) => (
                          <field.SwitchInput
                            label="Override Header & Navigation"
                            description="When enabled, this design replaces default header/navigation settings."
                          />
                        )}
                      </form.AppField>

                      <form.Subscribe
                        selector={(state) => state.values.section_overrides?.header_banner}
                      >
                        {(isEnabled) => (
                          <OverrideSectionState
                            isEnabled={!!isEnabled}
                            disabledLabel="Using default header & navigation"
                          >
                            <HeaderSectionForm />
                          </OverrideSectionState>
                        )}
                      </form.Subscribe>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Flower className="mr-2 size-6" />
                          Homepage banner
                        </div>
                      }
                      description="Override the homepage banner image and wording."
                      persistKey="non-default-hero-banner"
                    >
                      <form.AppField name="section_overrides.hero">
                        {(field) => (
                          <field.SwitchInput
                            label="Override homepage banner"
                            description="When enabled, this design replaces the everyday homepage banner."
                          />
                        )}
                      </form.AppField>

                      <form.Subscribe selector={(state) => state.values.section_overrides?.hero}>
                        {(isEnabled) => (
                          <OverrideSectionState
                            isEnabled={!!isEnabled}
                            disabledLabel="Using everyday homepage banner"
                          >
                            <HeroSectionForm allowInheritedLayout={allowInheritedHeroLayout} />
                          </OverrideSectionState>
                        )}
                      </form.Subscribe>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Star className="mr-2 size-6" />
                          Seasonal Featured Collection
                        </div>
                      }
                      description="Feature one timely collection first while keeping everyday sections like Sympathy, Birthday, reviews, and Contact Us in place."
                      persistKey="non-default-featured-content"
                    >
                      <form.AppField name="section_overrides.featured_content">
                        {(field) => (
                          <field.SwitchInput
                            label="Use seasonal featured collection"
                            description="Your everyday featured content stays below it, so you do not have to rebuild the homepage for a holiday."
                          />
                        )}
                      </form.AppField>

                      <form.Subscribe
                        selector={(state) => state.values.section_overrides?.featured_content}
                      >
                        {(isEnabled) => (
                          <OverrideSectionState
                            isEnabled={!!isEnabled}
                            disabledLabel="Using default featured content"
                          >
                            <SeasonalFeaturedCollectionForm
                              pageSlug="/"
                              isEnabled={!!isEnabled}
                              collectionMediaDraftController={collectionMediaDraftController}
                            />
                          </OverrideSectionState>
                        )}
                      </form.Subscribe>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title={
                        <div className="inline-flex items-center">
                          <Calendar className="mr-2 size-6" />
                          Schedule
                        </div>
                      }
                      description="Choose when this design should go live. When the schedule ends, your store automatically returns to your default design."
                      persistKey="non-default-schedule"
                    >
                      <ScheduleForm />
                    </CollapsibleCard>
                  </div>
                </>
              )}
            </>
          )}

          {(!isReadOnly || canEditStore) && (
            <SaveBar
              mutation={mutation}
              onReset={handleReset}
              forceDirty={hasWorkspaceDraft || collectionMediaDraftController.hasDrafts}
              shouldBlockNavigation={shouldBlockDesignNavigation}
              isInteractionPending={isSeasonalWorkspaceMode && isSeasonalRecommendationPending}
            />
          )}
        </form.Form>
      </form.AppForm>

      {!isSeasonalWorkspaceMode && deleteDesignAction}
    </div>
  )
}

export default DesignForm
